import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { AdminPageHeader, Form, AdminFormAction } from '@ui';
import { useAuthContext } from '@lp/core/auth';
import { EditRegionForm, EditRegionFormType, regionFormSchema } from '@lp/features/admin/forms/EditRegionForm';
import { RegionType } from '@/api/apis/facilities';
import { retrieveUnifiedError } from '@/api/utils';
import { useUpdateRegionMutation } from '@/queries/regions/regions.mutation';
import { useLoaderData } from '@/hooks/useLoaderData';
import { AdminPermissions } from '@/permissions';

const BACK_LINK_PATH = '/admin/regions';

export const RegionEdit = () => {
  const navigate = useNavigate();
  const data = useLoaderData<RegionType>();
  const { mutateAsync } = useUpdateRegionMutation(data.id);
  const { checkPermission } = useAuthContext();

  const hasEditPermissions = checkPermission(AdminPermissions.RegionsUpdate);

  const defaultValues = useMemo<EditRegionFormType>(() => {
    const { name, nationalManagers, regionalManagers, hubs } = data;

    return {
      name,
      nationalManagers: nationalManagers || [],
      regionalManagers: regionalManagers || [],
      hubs: hubs || [],
    };
  }, [data]);

  const handleBackToList = () => {
    navigate(BACK_LINK_PATH);
  };

  const handleSubmit = async (values: EditRegionFormType) => {
    try {
      const body = {
        regionalManagerIds: values.regionalManagers.map((m) => m.id) || [],
        nationalManagerIds: values.nationalManagers.map((m) => m.id) || [],
      };

      await mutateAsync(body);
      toast.success(`Region updated successfully.`);
      handleBackToList();
    } catch (e) {
      const error = retrieveUnifiedError(e);
      toast.error(error.message);
    }
  };

  return (
    <Form<EditRegionFormType> defaultValues={defaultValues} schema={regionFormSchema} onSubmit={handleSubmit}>
      <div className="flex flex-col gap-9">
        <AdminPageHeader title={hasEditPermissions ? 'Edit Region' : 'View Region'} backToListLink={BACK_LINK_PATH}>
          {hasEditPermissions && (
            <AdminFormAction
              type="save"
              showDeleteButton
              handleClick={handleBackToList}
              hasEditPermissions={hasEditPermissions}
            />
          )}
        </AdminPageHeader>
        <EditRegionForm isReadOnly={!hasEditPermissions} />
      </div>
    </Form>
  );
};
